import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, FlaskConical, Plus, Send, Trash2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { errorMessage, useFetch } from '../../services/operatorApi';
import { Button, Card, ErrorBox, Field, PageHeader, Spinner, inputClass, num } from '../../components/ui';

const MAX_BYTES = 25 * 1024 * 1024;

interface Credits {
  billing_mode: 'free' | 'prepaid';
  available: number;
}
interface SignerForm {
  id: number;
  full_name: string;
  email: string;
  role: string;
  channel: 'email' | 'sms' | 'email_sms';
  phone: string;
  where: 'auto' | 'text';
  marker: string;
}
interface Preview {
  url: string;
  filename: string;
  warnings: string[];
  placements: { signer: string; page: number; source: 'coordinates' | 'marker' | 'signature_page' }[];
}

const SOURCE: Record<Preview['placements'][number]['source'], string> = {
  marker: 'sur le texte repéré dans le PDF',
  signature_page: 'sur une page de signatures ajoutée à la fin',
  coordinates: 'à la position indiquée',
};

let nextId = 1;
const blankSigner = (): SignerForm => ({ id: nextId++, full_name: '', email: '', role: '', channel: 'email', phone: '', where: 'auto', marker: '' });

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.readAsDataURL(file);
  });
}

export default function NewDocumentPage() {
  const navigate = useNavigate();
  const { data: credits, error, loading, reload } = useFetch(async () => (await api.get<Credits>('/credits')).data);
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null);
  const [title, setTitle] = useState('');
  const [reference, setReference] = useState('');
  const [signers, setSigners] = useState<SignerForm[]>([blankSigner()]);
  const [mode, setMode] = useState<'parallel' | 'sequential'>('parallel');
  const [style, setStyle] = useState<'draw_or_type' | 'draw' | 'type'>('draw_or_type');
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState<'preview' | 'send' | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  if (loading && !credits) return <Spinner />;
  if (error || !credits) return <ErrorBox message={error ?? 'Chargement impossible'} onRetry={reload} />;

  const prepaid = credits.billing_mode === 'prepaid';
  const needed = signers.length;
  const short = prepaid && credits.available < needed;

  const update = (id: number, patch: Partial<SignerForm>) => setSigners((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const forgetPreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  async function pick(chosen: File | undefined) {
    setFailure(null);
    forgetPreview();
    if (!chosen) return;
    if (chosen.type !== 'application/pdf' && !chosen.name.toLowerCase().endsWith('.pdf')) return setFailure('Le fichier doit être un PDF.');
    if (chosen.size > MAX_BYTES) return setFailure('Le PDF dépasse 25 Mo.');
    try {
      setFile({ name: chosen.name, base64: await readBase64(chosen) });
      if (!title) setTitle(chosen.name.replace(/\.pdf$/i, ''));
    } catch (e) {
      setFailure(errorMessage(e));
    }
  }

  /** Problèmes de saisie communs aux deux actions ; `forReal` ajoute e-mail et téléphone, inutiles pour un essai. */
  function problem(forReal: boolean): string | null {
    if (!file) return 'Choisissez le PDF à faire signer.';
    if (!title.trim()) return 'Donnez un titre au document.';
    const markers = new Set<string>();
    for (const [index, s] of signers.entries()) {
      const who = `Signataire ${index + 1}`;
      if (!s.full_name.trim()) return `${who} : saisissez le nom.`;
      if (s.where === 'text') {
        const text = s.marker.trim();
        if (!text) return `${who} : indiquez le texte du PDF à remplacer par la signature.`;
        if (markers.has(text)) return `${who} : ce texte est déjà utilisé par un autre signataire.`;
        markers.add(text);
      }
      if (!forReal) continue;
      if (!/^\S+@\S+\.\S+$/.test(s.email.trim())) return `${who} : adresse e-mail invalide.`;
      if (s.channel !== 'email' && !/^\+[1-9]\d{6,14}$/.test(s.phone.trim())) return `${who} : le téléphone doit être au format international (+33612345678).`;
    }
    return null;
  }

  async function runPreview() {
    const issue = problem(false);
    if (issue) return setFailure(issue);
    setBusy('preview');
    setFailure(null);
    forgetPreview();
    try {
      const { data } = await api.post<{
        pdf_base64: string;
        filename: string;
        warnings: string[];
        placements: Preview['placements'];
      }>(
        '/documents/preview',
        {
          title: title.trim(),
          file_content: file!.base64,
          signers: signers.map((s, index) => ({
            full_name: s.full_name.trim(),
            role: s.role.trim() || undefined,
            order_index: index,
            signature_marker: s.where === 'text' ? s.marker.trim() : undefined,
          })),
        },
        { timeout: 120_000 }
      );
      const bytes = Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      setPreview({ url, filename: data.filename, warnings: data.warnings, placements: data.placements });
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setFailure(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function send() {
    const issue = problem(true);
    if (issue) return setFailure(issue);
    const plural = needed > 1 ? 's' : '';
    const cost = prepaid ? `\n\n${needed} signature${plural} seront décomptées de votre solde (${num(credits!.available)} disponibles).` : '';
    if (!confirm(`Envoyer « ${title.trim()} » à ${needed} signataire${plural} ? Chacun reçoit un lien par e-mail.${cost}`)) return;
    setBusy('send');
    setFailure(null);
    try {
      const created = await api.post<{ id: string }>(
        '/documents',
        {
          title: title.trim(),
          external_ref: reference.trim() || undefined,
          file_content: file!.base64,
          signing_mode: mode,
          signature_style: style,
          expires_in: days * 86400,
          signers: signers.map((s, index) => ({
            full_name: s.full_name.trim(),
            email: s.email.trim(),
            role: s.role.trim() || undefined,
            order_index: index,
            otp_channel: s.channel,
            phone: s.channel !== 'email' ? s.phone.trim() : undefined,
            signature_marker: s.where === 'text' ? s.marker.trim() : undefined,
          })),
        },
        { timeout: 120_000 }
      );
      await api.post(`/documents/${created.data.id}/send`, {});
      navigate('/documents', { state: { sent: title.trim() } });
    } catch (e) {
      setFailure(errorMessage(e));
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Nouveau document" subtitle="Déposez un PDF, indiquez qui signe et où, puis envoyez-le ou testez le rendu" />

      <Card title="1. Le document">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => void pick(e.target.files?.[0])} />
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-left hover:border-indigo-400 hover:bg-indigo-50/40"
            >
              <FileText className="h-8 w-8 shrink-0 text-indigo-500" />
              <span className="text-sm">
                {file ? <><strong className="text-slate-900">{file.name}</strong><br /><span className="text-slate-500">Cliquez pour choisir un autre PDF</span></> : <><strong className="text-slate-900">Choisir le PDF à faire signer</strong><br /><span className="text-slate-500">25 Mo maximum</span></>}
              </span>
            </button>
          </div>
          <Field label="Titre">
            <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500} placeholder="Contrat de prestation" />
          </Field>
          <Field label="Référence interne (facultatif)" hint="Votre numéro de commande ou de dossier, pour le retrouver dans la liste.">
            <input className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} maxLength={255} />
          </Field>
        </div>
      </Card>

      <Card title="2. Les signataires">
        <div className="space-y-4">
          {signers.map((s, index) => (
            <div key={s.id} className="rounded-xl border border-slate-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">Signataire {index + 1}</span>
                {signers.length > 1 && (
                  <button type="button" className="text-slate-400 hover:text-red-600" aria-label={`Retirer le signataire ${index + 1}`} onClick={() => { setSigners((rows) => rows.filter((r) => r.id !== s.id)); forgetPreview(); }}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <Field label="Nom complet">
                  <input className={inputClass} value={s.full_name} onChange={(e) => update(s.id, { full_name: e.target.value })} maxLength={255} />
                </Field>
                <Field label="E-mail" hint="Inutile pour un essai.">
                  <input className={inputClass} type="email" value={s.email} onChange={(e) => update(s.id, { email: e.target.value })} />
                </Field>
                <Field label="Rôle (facultatif)">
                  <input className={inputClass} value={s.role} onChange={(e) => update(s.id, { role: e.target.value })} maxLength={100} placeholder="Client, Prestataire…" />
                </Field>
                <Field
                  label="Comment le signataire confirme son identité"
                  hint={{
                    email: 'Il reçoit un e-mail avec un lien personnel et signe directement, sans code.',
                    sms: 'Il reçoit un SMS avec un lien personnel et signe directement, sans code.',
                    email_sms: 'Double sécurité : un lien par e-mail, puis un code reçu par SMS à saisir avant de signer.',
                  }[s.channel]}
                >
                  <select className={inputClass} value={s.channel} onChange={(e) => update(s.id, { channel: e.target.value as SignerForm['channel'] })}>
                    <option value="email">Par e-mail (recommandé)</option>
                    <option value="sms">Par SMS</option>
                    <option value="email_sms">Double sécurité : e-mail + code SMS</option>
                  </select>
                </Field>
                {s.channel !== 'email' && (
                  <Field label="Téléphone" hint="Format international">
                    <input className={inputClass} value={s.phone} onChange={(e) => update(s.id, { phone: e.target.value })} placeholder="+33612345678" />
                  </Field>
                )}
                <Field label="Emplacement de la signature">
                  <select className={inputClass} value={s.where} onChange={(e) => update(s.id, { where: e.target.value as SignerForm['where'] })}>
                    <option value="auto">Automatique</option>
                    <option value="text">Remplacer un texte du PDF</option>
                  </select>
                </Field>
                {s.where === 'text' && (
                  <Field label="Texte à remplacer" hint="Recopiez-le exactement, il doit apparaître une seule fois.">
                    <input className={inputClass} value={s.marker} onChange={(e) => update(s.id, { marker: e.target.value })} maxLength={100} placeholder="[SIGNATURE_CLIENT]" />
                  </Field>
                )}
              </div>
            </div>
          ))}
          <Button variant="secondary" onClick={() => { setSigners((rows) => [...rows, blankSigner()]); forgetPreview(); }} disabled={signers.length >= 50}>
            <Plus className="h-4 w-4" />Ajouter un signataire
          </Button>
        </div>
        <p className="mt-4 rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
          <strong className="text-slate-800">Où apparaît la signature ?</strong> Avec « Remplacer un texte », le cadre de signature recouvre ce texte (par exemple <code className="rounded bg-white px-1">[SIGNATURE_CLIENT]</code>, écrit dans votre PDF à l&apos;endroit voulu).
          En automatique, nous cherchons <code className="rounded bg-white px-1">{'{{sig_1}}'}</code>, <code className="rounded bg-white px-1">{'{{sig_2}}'}</code>… (le numéro est l&apos;ordre du signataire dans cette liste). Sans texte repéré, une page de signatures est ajoutée à la fin du document.
        </p>
      </Card>

      <Card title="3. Options">
        <div className="grid gap-4 md:grid-cols-3">
          {signers.length > 1 && (
            <Field label="Ordre de signature">
              <select className={inputClass} value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
                <option value="parallel">Tous en même temps</option>
                <option value="sequential">L&apos;un après l&apos;autre, dans l&apos;ordre de la liste</option>
              </select>
            </Field>
          )}
          <Field label="Apparence de la signature">
            <select className={inputClass} value={style} onChange={(e) => setStyle(e.target.value as typeof style)}>
              <option value="draw_or_type">Le signataire trace ou tape son nom</option>
              <option value="draw">Tracé à la main</option>
              <option value="type">Nom tapé</option>
            </select>
          </Field>
          <Field label="Validité du lien (jours)">
            <input className={inputClass} type="number" min={1} max={30} value={days} onChange={(e) => setDays(Math.max(1, Math.min(30, Number(e.target.value) || 30)))} />
          </Field>
        </div>
      </Card>

      {failure && <ErrorBox message={failure} />}

      {preview && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-950">
          <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />Document d&apos;essai généré : NON VALIDE</p>
          <p className="mt-1">Il s&apos;ouvre dans un nouvel onglet. Rien n&apos;a été envoyé ni enregistré, aucune signature n&apos;a été décomptée, et chaque page porte la mention « NON VALIDE ».</p>
          <ul className="mt-3 list-disc space-y-1 pl-5">
            {preview.placements.map((p) => <li key={p.signer}><strong>{p.signer}</strong> : page {p.page}, {SOURCE[p.source]}</li>)}
          </ul>
          {preview.warnings.map((w) => <p key={w} className="mt-2 text-amber-800">{w}</p>)}
          <div className="mt-3 flex flex-wrap gap-3">
            <a className="font-medium text-indigo-700 hover:underline" href={preview.url} target="_blank" rel="noreferrer">Rouvrir</a>
            <a className="font-medium text-indigo-700 hover:underline" href={preview.url} download={preview.filename}>Télécharger</a>
          </div>
        </div>
      )}

      <Card>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><FlaskConical className="h-4 w-4 text-amber-600" />Tester le rendu</h3>
            <p className="mt-1 text-sm text-slate-600">Simule la signature de chacun et vous rend un PDF marqué « NON VALIDE ». Gratuit, sans e-mail ni SMS, rien n&apos;est enregistré. L&apos;e-mail des signataires n&apos;est pas nécessaire.</p>
            <div className="mt-3"><Button variant="secondary" onClick={runPreview} loading={busy === 'preview'} disabled={busy !== null}>Générer le document d&apos;essai</Button></div>
          </div>
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Send className="h-4 w-4 text-indigo-600" />Envoyer à la signature</h3>
            <p className="mt-1 text-sm text-slate-600">
              Chaque signataire reçoit un lien par e-mail.{' '}
              {prepaid
                ? <>Une signature est décomptée par signataire une fois signé : <strong>{needed}</strong> pour ce document, <strong className={short ? 'text-red-600' : ''}>{num(credits.available)}</strong> disponibles.</>
                : 'Votre compte n’est pas facturé à la signature.'}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button onClick={send} loading={busy === 'send'} disabled={busy !== null || short}>Envoyer à {needed} signataire{needed > 1 ? 's' : ''}</Button>
              {short && <Link to="/billing" className="text-sm font-medium text-indigo-600 hover:underline">Solde insuffisant : acheter un lot</Link>}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
